import { useState, useRef, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import Drawer from '../Common/Drawer';
import { InputFormik, SearchableDropdown, Button, Dropdown, DatePickerInputFormik, Checkbox } from '../Common';
import { ProjectRoleDto, ProjectAssigneesWithUserInfoDto, EmploymentType } from '../../commonTypes/project.types';
import { RoleTypeEnum } from '../../commonTypes/user.types';
import { RateType } from '@cyberlabrs/helpers';
import { validationSchema } from './validationSchema';
import { getCurrencySymbol } from '../../util/general';
import { useTranslation } from '../../hooks/useTranslation';
import { intervalDateToApiString } from '../../util/projectAssigneeRate/effectiveFrom';
import { mappedCurrency } from '../ClientForms/utils/general';

const BILLABLE_OPTIONS = [
    { label: 'Billable', value: 'billable' },
    { label: 'Non-billable', value: 'non-billable' },
];

const RATE_TYPE_OPTIONS = [
    { label: 'Hourly', value: RateType.HOURLY },
    { label: 'Fixed', value: RateType.FIXED },
];

const DEFAULT_RATE = '0';
const DEFAULT_EMPLOYMENT_TYPE = EmploymentType.FULL_TIME;
const DEFAULT_RATE_TYPE = RateType.HOURLY;
const DEFAULT_BILLABLE_TYPE = 'billable';
const ALLOWED_MANAGER_ROLE_NAMES = new Set<RoleTypeEnum>([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN, RoleTypeEnum.PM]);
const RATE_INPUT_REGEX = /^\d*(\.\d{0,4})?$/;

const normalizeDateOnly = (value?: string, fallback?: string) => {
    if (!value) return fallback || '';
    const trimmed = value.trim();
    const ymdMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (ymdMatch) {
        return ymdMatch[1];
    }

    const parsed = DateTime.fromISO(trimmed);
    if (!parsed.isValid) return fallback || value;
    return parsed.toFormat('yyyy-MM-dd');
};

type AvailableMember = {
    label: string;
    value: string;
    imageUrl?: string;
    jobTitle?: string;
    roleName?: RoleTypeEnum;
    wasPreviouslyAssigned?: boolean;
    suggestedStartDate?: string;
    minimumStartDate?: string;
};

type CompensationIntervalInitialState = {
    compensationIntervalStart: Date | null;
    compensationIntervalEnd: Date | null;
    compensationIntervalOpenEnded: boolean;
};

interface IMemberAssignment {
    memberId: string;
    role: string | number;
    billableType: string;
    rateType: string;
    employmentType: string;
    currencyCode: string;
    rate: string;
    startDate: string;
}

export type AssignMembersSubmitOptions = {
    compensationIntervalStart?: string;
    compensationIntervalEnd?: string;
};

interface IAssignMembersDrawerProps {
    open: boolean;
    onClose: () => void;
    onAssign: (members: IMemberAssignment[], options?: AssignMembersSubmitOptions) => void | boolean | Promise<void | boolean>;
    onUnassign?: () => void;
    availableMembers: AvailableMember[];
    employmentTypes: { label: string; value: string }[];
    projectRoles: ProjectRoleDto[];
    editMode?: boolean;
    editMember?: ProjectAssigneesWithUserInfoDto;
    currency: string;
    projectStartDate?: string;
    projectEndDate?: string | null;
}

const AssignMembersDrawer = ({
    open,
    onClose,
    onAssign,
    onUnassign,
    availableMembers,
    employmentTypes,
    projectRoles,
    editMode = false,
    editMember,
    currency,
    projectStartDate,
    projectEndDate,
}: IAssignMembersDrawerProps) => {
    const { t } = useTranslation();
    const intervalPlaceholderDate = useMemo(() => DateTime.utc().toFormat('MMMM d, yyyy'), []);
    const defaultAssigneeStartDate = useMemo(() => {
        const normalizedProjectStart = normalizeDateOnly(projectStartDate);
        if (normalizedProjectStart) {
            return normalizedProjectStart;
        }

        return DateTime.local().toFormat('yyyy-MM-dd');
    }, [projectStartDate]);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const formatRoleName = (name: string) => {
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    const roleOptions = projectRoles.map(role => ({ label: formatRoleName(role.name), value: role.id }));
    const managerRole = projectRoles.find(role => role.name === 'MANAGER');
    const assigneeRole = projectRoles.find(role => role.name === 'ASSIGNEE');

    const getInitialMemberAssignments = (): IMemberAssignment[] => {
        if (editMode && editMember) {
            const isBillable = editMember.billable;
            return [
                {
                    memberId: editMember.userSub,
                    role: editMember.assignedRoles?.[0]?.projectRoleDto?.id?.toString() || '',
                    billableType: isBillable ? DEFAULT_BILLABLE_TYPE : 'non-billable',
                    rateType: (editMember as any).rateType || DEFAULT_RATE_TYPE,
                    employmentType: (editMember as any).employmentType || DEFAULT_EMPLOYMENT_TYPE,
                    currencyCode: editMember.currencyCode || currency,
                    rate: editMember.rate?.toString() || '',
                    startDate: normalizeDateOnly(editMember.startDate, defaultAssigneeStartDate),
                },
            ];
        }
        return [
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                currencyCode: currency,
                rate: DEFAULT_RATE,
                startDate: defaultAssigneeStartDate,
            },
        ];
    };

    const [memberAssignments, setMemberAssignments] = useState<IMemberAssignment[]>(getInitialMemberAssignments());
    const lastFormRef = useRef<HTMLDivElement>(null);
    const initialMemberAssignmentsRef = useRef<IMemberAssignment[] | null>(null);
    const initialCompensationIntervalRef = useRef<CompensationIntervalInitialState | null>(null);

    const parseEffectiveFromDate = (value?: string): Date | null => {
        const normalized = normalizeDateOnly(value);
        if (!normalized) return null;
        const parsed = DateTime.fromISO(normalized, { zone: 'local' });
        return parsed.isValid ? parsed.startOf('day').toJSDate() : null;
    };

    const getInitialCompensationIntervalValues = (): CompensationIntervalInitialState => {
        if (!editMode || !editMember) {
            return {
                compensationIntervalStart: null,
                compensationIntervalEnd: null,
                compensationIntervalOpenEnded: true,
            };
        }

        const intervalStart = parseEffectiveFromDate(editMember.compensationIntervalStart);
        const intervalEnd = parseEffectiveFromDate(editMember.compensationIntervalEnd ?? undefined);

        return {
            compensationIntervalStart: intervalStart ?? null,
            compensationIntervalEnd: intervalEnd ?? null,
            compensationIntervalOpenEnded: intervalEnd == null,
        };
    };

    const findRole = (roleId: string | number) => {
        return roleOptions.find(role => role.value === roleId) || { label: '', value: '' };
    };

    const findMember = (memberId: string) => {
        return availableMembers.find(member => member.value === memberId) || { label: '', value: '' };
    };

    const isManagerEligibleMember = (memberId: string) => {
        const member = availableMembers.find(item => item.value === memberId);
        return member?.roleName ? ALLOWED_MANAGER_ROLE_NAMES.has(member.roleName) : true;
    };

    const getRoleOptionsForMember = (memberId: string) => {
        if (!managerRole || isManagerEligibleMember(memberId)) {
            return roleOptions;
        }

        return roleOptions.filter(role => role.value !== managerRole.id);
    };

    const findEmploymentType = (typeId: string) => {
        return employmentTypes.find(type => type.value === typeId) || { label: '', value: '' };
    };

    const findBillableType = (billableTypeId: string) => {
        return BILLABLE_OPTIONS.find(type => type.value === billableTypeId) || { label: '', value: '' };
    };

    const findRateType = (rateTypeId: string) => {
        return RATE_TYPE_OPTIONS.find(type => type.value === rateTypeId) || { label: '', value: '' };
    };

    const context = useFormik({
        initialValues: {
            members: memberAssignments,
            ...getInitialCompensationIntervalValues(),
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async values => {
            if (editMode && values.compensationIntervalStart) {
                const maxYmd = projectEndYmd;
                const intervalStart = intervalDateToApiString(values.compensationIntervalStart, maxYmd);
                const intervalEnd = values.compensationIntervalOpenEnded
                    ? undefined
                    : intervalDateToApiString(values.compensationIntervalEnd, maxYmd);
                if (intervalStart === 'AFTER_MAX_DATE') {
                    void context.setFieldTouched('compensationIntervalStart', true);
                    void context.setFieldError('compensationIntervalStart', t('projects.rateCorrectionEffectiveFromAfterProjectEndDate'));
                    return;
                }
                if (intervalEnd === 'AFTER_MAX_DATE') {
                    void context.setFieldTouched('compensationIntervalEnd', true);
                    void context.setFieldError('compensationIntervalEnd', t('projects.rateCorrectionEffectiveFromAfterProjectEndDate'));
                    return;
                }
                const shouldClose = await onAssign(
                    values.members,
                    intervalStart
                        ? {
                              compensationIntervalStart: intervalStart,
                              ...(intervalEnd ? { compensationIntervalEnd: intervalEnd } : {}),
                          }
                        : undefined
                );
                if (shouldClose !== false) handleClose();
            } else {
                const shouldClose = await onAssign(values.members);
                if (shouldClose !== false) handleClose();
            }
        },
    });

    useEffect(() => {
        if (lastFormRef.current && memberAssignments.length > 1) {
            lastFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [memberAssignments.length]);

    useEffect(() => {
        if (open) {
            const initialAssignments = getInitialMemberAssignments();
            setMemberAssignments(initialAssignments);
            initialMemberAssignmentsRef.current = JSON.parse(JSON.stringify(initialAssignments));
            const initialCompensationInterval = getInitialCompensationIntervalValues();
            initialCompensationIntervalRef.current = {
                compensationIntervalStart: initialCompensationInterval.compensationIntervalStart
                    ? new Date(initialCompensationInterval.compensationIntervalStart)
                    : null,
                compensationIntervalEnd: initialCompensationInterval.compensationIntervalEnd
                    ? new Date(initialCompensationInterval.compensationIntervalEnd)
                    : null,
                compensationIntervalOpenEnded: initialCompensationInterval.compensationIntervalOpenEnded,
            };
            setIsAdvancedOpen(false);
            void context.setValues({
                members: initialAssignments,
                ...initialCompensationInterval,
            });
        }
    }, [open, editMode, editMember]);

    const handleClose = () => {
        setMemberAssignments([
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                currencyCode: currency,
                rate: DEFAULT_RATE,
                startDate: defaultAssigneeStartDate,
            },
        ]);
        context.resetForm();
        initialMemberAssignmentsRef.current = null;
        initialCompensationIntervalRef.current = null;
        setIsAdvancedOpen(false);
        onClose();
    };

    const handleAddMoreMembers = () => {
        setMemberAssignments([
            ...memberAssignments,
            {
                memberId: '',
                role: '',
                billableType: DEFAULT_BILLABLE_TYPE,
                rateType: DEFAULT_RATE_TYPE,
                employmentType: DEFAULT_EMPLOYMENT_TYPE,
                currencyCode: currency,
                rate: DEFAULT_RATE,
                startDate: defaultAssigneeStartDate,
            },
        ]);
    };

    const handleRemoveMember = (index: number) => {
        const updatedMembers = memberAssignments.filter((_, i) => i !== index);
        setMemberAssignments(updatedMembers);
    };

    const updateMemberField = (index: number, field: keyof IMemberAssignment, value: string) => {
        const updatedMembers = [...memberAssignments];
        if (field === 'rate') {
            const normalizedValue = value.replace(',', '.');
            if (normalizedValue !== '' && !RATE_INPUT_REGEX.test(normalizedValue)) {
                return;
            }
            value = normalizedValue;
        }
        updatedMembers[index][field] = value;

        if (field === 'memberId' && managerRole && updatedMembers[index].role === managerRole.id.toString()) {
            if (!isManagerEligibleMember(value)) {
                updatedMembers[index].role = assigneeRole?.id?.toString() || '';
            }
        }

        if (field === 'billableType' && value === 'non-billable') {
            updatedMembers[index].rateType = DEFAULT_RATE_TYPE;
            updatedMembers[index].employmentType = DEFAULT_EMPLOYMENT_TYPE;
            updatedMembers[index].rate = '';
        } else if (field === 'billableType' && value === 'billable') {
            if (!updatedMembers[index].rate || updatedMembers[index].rate.trim() === '') {
                updatedMembers[index].rate = DEFAULT_RATE;
            }
            if (!updatedMembers[index].employmentType || updatedMembers[index].employmentType.trim() === '') {
                updatedMembers[index].employmentType = DEFAULT_EMPLOYMENT_TYPE;
            }
            if (!updatedMembers[index].rateType) {
                updatedMembers[index].rateType = DEFAULT_RATE_TYPE;
            }
        }

        setMemberAssignments(updatedMembers);
        void context.setFieldValue('members', updatedMembers);
    };

    const handleMemberSelection = (index: number, memberItem: AvailableMember) => {
        const updatedMembers = [...memberAssignments];
        updatedMembers[index].memberId = memberItem.value;
        updatedMembers[index].startDate = memberItem.suggestedStartDate || memberItem.minimumStartDate || defaultAssigneeStartDate;

        if (managerRole && updatedMembers[index].role === managerRole.id.toString()) {
            if (!isManagerEligibleMember(memberItem.value)) {
                updatedMembers[index].role = assigneeRole?.id?.toString() || '';
            }
        }

        setMemberAssignments(updatedMembers);
        void context.setFieldValue('members', updatedMembers);
    };

    const projectStartYmd = useMemo(() => normalizeDateOnly(projectStartDate) || undefined, [projectStartDate]);
    const projectEndYmd = useMemo(() => (projectEndDate ? normalizeDateOnly(projectEndDate) || undefined : undefined), [projectEndDate]);

    const getMinimumStartDateForMember = (memberId: string) => {
        const selectedMember = availableMembers.find(member => member.value === memberId);
        const memberMin = selectedMember?.minimumStartDate || defaultAssigneeStartDate;
        if (projectStartYmd && memberMin < projectStartYmd) return projectStartYmd;
        return memberMin;
    };

    const compensationIntervalMaxYmd = useMemo(() => {
        return projectEndYmd;
    }, [projectEndYmd]);

    const getAvailableMembersForIndex = (currentIndex: number) => {
        const selectedMemberIds = memberAssignments
            .map((assignment, index) => (index !== currentIndex ? assignment.memberId : null))
            .filter(id => id !== null && id !== '');

        return availableMembers.filter(member => !selectedMemberIds.includes(member.value));
    };

    const isFormValid = () => {
        return memberAssignments.every(member => {
            const baseValid = member.memberId && member.role && member.billableType && member.currencyCode;
            const hasStartDate = Boolean(member.startDate);
            if (member.billableType === 'non-billable') {
                return baseValid && hasStartDate;
            }
            const normalizedRate = member.rate.trim();
            return (
                baseValid &&
                hasStartDate &&
                member.rateType &&
                member.employmentType &&
                normalizedRate &&
                RATE_INPUT_REGEX.test(normalizedRate) &&
                Number(normalizedRate) > 0
            );
        });
    };

    const hasFormChanges = () => {
        if (!editMode) {
            return true;
        }
        if (!initialMemberAssignmentsRef.current) {
            return true;
        }
        const membersChanged = JSON.stringify(memberAssignments) !== JSON.stringify(initialMemberAssignmentsRef.current);
        const initialCompensationInterval = initialCompensationIntervalRef.current || {
            compensationIntervalStart: null,
            compensationIntervalEnd: null,
            compensationIntervalOpenEnded: true,
        };
        const currentIntervalStart = context.values.compensationIntervalStart
            ? DateTime.fromJSDate(context.values.compensationIntervalStart).toUTC().toISODate()
            : null;
        const initialIntervalStart = initialCompensationInterval.compensationIntervalStart
            ? DateTime.fromJSDate(initialCompensationInterval.compensationIntervalStart).toUTC().toISODate()
            : null;
        const currentIntervalEnd = context.values.compensationIntervalEnd
            ? DateTime.fromJSDate(context.values.compensationIntervalEnd).toUTC().toISODate()
            : null;
        const initialIntervalEnd = initialCompensationInterval.compensationIntervalEnd
            ? DateTime.fromJSDate(initialCompensationInterval.compensationIntervalEnd).toUTC().toISODate()
            : null;
        return (
            membersChanged ||
            currentIntervalStart !== initialIntervalStart ||
            currentIntervalEnd !== initialIntervalEnd ||
            context.values.compensationIntervalOpenEnded !== initialCompensationInterval.compensationIntervalOpenEnded
        );
    };

    return (
        <Drawer
            open={open}
            title={editMode ? 'Edit assignee' : 'Assign Member'}
            onClose={handleClose}
            showFooter={true}
            className="assign-members-drawer"
            leftButtons={
                editMode && onUnassign
                    ? [
                          {
                              text: 'Unassign',
                              variant: 'text',
                              onClick: onUnassign,
                          },
                      ]
                    : undefined
            }
            rightButtons={[
                {
                    text: 'Cancel',
                    variant: 'text',
                    onClick: handleClose,
                },
                {
                    text: editMode ? 'Save' : 'Assign Member',
                    variant: 'contained',
                    onClick: () => context.handleSubmit(),
                    disabled: !isFormValid() || (editMode && !hasFormChanges()),
                },
            ]}
        >
            <div className="assign-members-content">
                {editMode && editMember && (
                    <div className="edit-member__info">
                        <img
                            src={editMember.imageUrl || '/svg/person.svg'}
                            alt={`${editMember.givenName} ${editMember.familyName}`}
                            className="edit-member__avatar"
                            onError={e => ((e.target as HTMLImageElement).src = '/svg/person.svg')}
                        />
                        <div className="edit-member__name">
                            {`${editMember.givenName || ''} ${editMember.familyName || ''}`.trim() || 'Unknown Member'}
                        </div>
                    </div>
                )}
                {!editMode && availableMembers.some(member => member.wasPreviouslyAssigned) && (
                    <p className="paragraph-5 assign-members-drawer__effective-from-hint">{t('projects.previouslyAssignedHint')}</p>
                )}
                {memberAssignments.map((member, index) => (
                    <div key={index} className="member-assignment-form" ref={index === memberAssignments.length - 1 ? lastFormRef : null}>
                        {!editMode && (
                            <SearchableDropdown
                                label="Select Member"
                                menuItems={getAvailableMembersForIndex(index)}
                                selected={findMember(member.memberId)}
                                onChange={item => handleMemberSelection(index, item as AvailableMember)}
                                isRequired
                                showImage
                            />
                        )}
                        {!editMode && (
                            <Dropdown
                                label="Role"
                                menuItems={getRoleOptionsForMember(member.memberId)}
                                selected={findRole(member.role)}
                                onChange={item => updateMemberField(index, 'role', item.value as string)}
                            />
                        )}
                        <Dropdown
                            label="Billable Type"
                            menuItems={BILLABLE_OPTIONS}
                            selected={findBillableType(member.billableType)}
                            onChange={item => updateMemberField(index, 'billableType', item.value as string)}
                        />

                        {member.billableType === 'billable' && (
                            <Dropdown
                                label="Rate Type"
                                menuItems={RATE_TYPE_OPTIONS}
                                selected={findRateType(member.rateType)}
                                onChange={item => updateMemberField(index, 'rateType', item.value as string)}
                            />
                        )}

                        <SearchableDropdown
                            label="Select Employment Type"
                            menuItems={employmentTypes}
                            selected={findEmploymentType(member.employmentType)}
                            onChange={item => updateMemberField(index, 'employmentType', item.value as string)}
                            isRequired
                            disabled={member.billableType === 'non-billable'}
                            className={member.employmentType === DEFAULT_EMPLOYMENT_TYPE ? 'dropdown--placeholder-value' : ''}
                        />

                        <DatePickerInputFormik
                            className="assign-members-drawer__effective-from-date"
                            context={context}
                            id={`members.${index}.startDate`}
                            label="Start date"
                            labelInfoText="This date is used as the assignee billing start date."
                            placeholder={t('projects.rateCorrectionEffectiveFromPlaceholder', {
                                date: intervalPlaceholderDate,
                            })}
                            date={member.startDate ? DateTime.fromISO(member.startDate).toJSDate() : undefined}
                            changeDate={date => updateMemberField(index, 'startDate', date?.toFormat('yyyy-MM-dd') ?? '')}
                            onPeriodChange={() => {}}
                            enablePastDays={true}
                            isRequired
                            minUtcDateString={getMinimumStartDateForMember(member.memberId)}
                            maxUtcDateString={projectEndYmd}
                        />

                        <InputFormik
                            context={context}
                            id={`members.${index}.rate`}
                            label={`Rate (${getCurrencySymbol(member.currencyCode || currency)})`}
                            placeholder="Enter Hourly Rate"
                            value={member.rate}
                            onChange={e => updateMemberField(index, 'rate', e.target.value)}
                            isRequired
                            isDisabled={member.billableType === 'non-billable'}
                            className={member.rate === DEFAULT_RATE ? 'input-style--placeholder-value' : ''}
                        />

                        {editMode && (
                            <Dropdown
                                label="Currency"
                                menuItems={mappedCurrency}
                                selected={mappedCurrency.find(item => item.value === member.currencyCode) || { label: '', value: '' }}
                                onChange={item => updateMemberField(index, 'currencyCode', item.value as string)}
                            />
                        )}

                        {editMode && index === 0 && (
                            <div className="assign-members-drawer__effective-from-block">
                                <button
                                    type="button"
                                    className="assign-members-drawer__advanced-toggle"
                                    onClick={() => setIsAdvancedOpen(prev => !prev)}
                                    aria-expanded={isAdvancedOpen}
                                >
                                    <span className="paragraph-4">Advanced settings</span>
                                    <img
                                        src="/img/header2/arrow-down.svg"
                                        alt=""
                                        aria-hidden="true"
                                        className={`assign-members-drawer__advanced-icon ${isAdvancedOpen ? 'is-open' : ''}`}
                                    />
                                </button>
                                {isAdvancedOpen && (
                                    <>
                                        <p className="assign-members-drawer__effective-from-hint paragraph-5">{t('projects.effectiveFromHint')}</p>
                                        <DatePickerInputFormik
                                            className="assign-members-drawer__effective-from-date"
                                            context={context}
                                            id="compensationIntervalStart"
                                            label="From"
                                            labelInfoText="This is the first day for the rate and currency update interval."
                                            placeholder={t('projects.rateCorrectionEffectiveFromPlaceholder', {
                                                date: intervalPlaceholderDate,
                                            })}
                                            date={context.values.compensationIntervalStart || undefined}
                                            changeDate={date => void context.setFieldValue('compensationIntervalStart', date?.toJSDate() ?? null)}
                                            onPeriodChange={() => {}}
                                            enablePastDays={true}
                                            isRequired={false}
                                            minUtcDateString={projectStartYmd}
                                            maxUtcDateString={compensationIntervalMaxYmd}
                                            clearData={() => {
                                                void context.setFieldValue('compensationIntervalStart', null);
                                                void context.setFieldValue('compensationIntervalEnd', null);
                                                void context.setFieldValue('compensationIntervalOpenEnded', true);
                                            }}
                                        />
                                        <Checkbox
                                            id="compensationIntervalOpenEnded"
                                            label={t('projects.assigneeRatePeriodNoEndDate')}
                                            checked={context.values.compensationIntervalOpenEnded}
                                            handleChange={e => {
                                                void context.setFieldValue('compensationIntervalOpenEnded', e.target.checked);
                                                if (e.target.checked) {
                                                    void context.setFieldValue('compensationIntervalEnd', null);
                                                }
                                            }}
                                            className="assign-members-drawer__interval-toggle"
                                        />
                                        <DatePickerInputFormik
                                            className="assign-members-drawer__effective-from-date"
                                            context={context}
                                            id="compensationIntervalEnd"
                                            label="To"
                                            labelInfoText="Leave this open with Active enabled to apply the change from the selected start date onward."
                                            placeholder={t('projects.rateCorrectionEffectiveFromPlaceholder', {
                                                date: intervalPlaceholderDate,
                                            })}
                                            date={context.values.compensationIntervalEnd || undefined}
                                            changeDate={date => void context.setFieldValue('compensationIntervalEnd', date?.toJSDate() ?? null)}
                                            onPeriodChange={() => {}}
                                            enablePastDays={true}
                                            isRequired={false}
                                            disabled={context.values.compensationIntervalOpenEnded}
                                            minUtcDateString={
                                                context.values.compensationIntervalStart
                                                    ? DateTime.fromJSDate(context.values.compensationIntervalStart).toFormat('yyyy-MM-dd')
                                                    : projectStartYmd
                                            }
                                            maxUtcDateString={compensationIntervalMaxYmd}
                                            clearData={() => void context.setFieldValue('compensationIntervalEnd', null)}
                                        />
                                    </>
                                )}
                            </div>
                        )}

                        {memberAssignments.length > 1 && (
                            <button type="button" className="remove-member-btn" onClick={() => handleRemoveMember(index)}>
                                X
                            </button>
                        )}
                    </div>
                ))}

                {!editMode && (
                    <Button text="Add More Members" variant="text" iconSrc="/svg/plus-blue-without-circle.svg" onClick={handleAddMoreMembers} />
                )}
            </div>
        </Drawer>
    );
};

export default AssignMembersDrawer;
